import { useCallback, useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { Check, Play, Square, X } from "lucide-react";
import { formatDay, formatDuration, formatMoment, localDay, statusLabel } from "@/lib/slots";

const ACTIVE = ["pending", "confirmed", "assigned", "rescheduled", "in_progress"];
const TABS = [["active", "Active"], ["completed", "Completed"], ["all", "All"]];
const PILL = {
  pending: "bg-amber-100 text-amber-900", rescheduled: "bg-amber-100 text-amber-900",
  confirmed: "bg-blue-100 text-blue-900", assigned: "bg-indigo-100 text-indigo-900",
  in_progress: "bg-pink-100 text-pink-900", completed: "bg-green-100 text-green-900",
  cancelled: "bg-red-100 text-red-900", rejected: "bg-red-100 text-red-900",
};

/**
 * What the professional can do with a job, by its status. The server checks
 * every one of these again (backend/scheduling.py): a button here is only a
 * way to ask.
 *
 *   waiting to be accepted   Accept · Reject
 *   confirmed / assigned     Start Task · Decline
 *   in progress              End Task
 */
const ACTIONS = {
  accept: { label: "Accept", busy: "Accepting…", done: "Job accepted", icon: Check, call: (id) => api.put(`/seller/bookings/${id}/status`, { status: "confirmed" }) },
  reject: { label: "Reject", busy: "Rejecting…", done: "Job rejected", icon: X, confirm: "Reject this job? The customer's time slot is given up.", call: (id) => api.put(`/seller/bookings/${id}/status`, { status: "rejected" }) },
  start: { label: "Start Task", busy: "Starting…", done: "Task started", icon: Play, call: (id) => api.post(`/seller/bookings/${id}/start`) },
  end: { label: "End Task", busy: "Ending…", done: "Task completed", icon: Square, confirm: "End this task? The booking is marked completed.", call: (id) => api.post(`/seller/bookings/${id}/end`) },
};
const actionsFor = (status) => {
  if (status === "pending" || status === "rescheduled") return ["accept", "reject"];
  if (status === "confirmed" || status === "assigned") return ["start", "reject"];
  if (status === "in_progress") return ["end"];
  return [];
};

export default function SellerBookings() {
  const [list, setList] = useState(null);
  const [tab, setTab] = useState("active");
  // { [booking id]: action being sent }, so a second tap cannot send it twice.
  const [busy, setBusy] = useState({});

  const load = useCallback(() => api.get("/seller/bookings").then((r) => setList(r.data)).catch(() => setList((l) => l || [])), []);
  useEffect(() => { load(); }, [load]);

  const act = async (b, kind) => {
    const action = ACTIONS[kind];
    if (busy[b.id]) return;
    if (action.confirm && !window.confirm(action.confirm)) return;
    // A job may be started before its day (work done early, by agreement), so
    // the server allows it. A tap by mistake is the likelier case: ask first.
    if (kind === "start" && b.scheduled_date > localDay()
      && !window.confirm(`This job is booked for ${formatDay(b.scheduled_date)}, ${b.scheduled_slot}. Start it now?`)) return;
    setBusy((x) => ({ ...x, [b.id]: kind }));
    try {
      await action.call(b.id);
      toast.success(action.done);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (!err?.response) {
        // No answer: the change may or may not have reached the server. Asking
        // again is safe, the server does it once.
        toast.error("No connection. Check the network and tap again.");
      } else {
        toast.error(typeof detail === "string" ? detail : "That did not work. Please try again.");
      }
    } finally {
      // Always show what the server holds now, whatever happened to the request.
      await load();
      setBusy((x) => { const next = { ...x }; delete next[b.id]; return next; });
    }
  };

  if (!list) return <div className="text-sm text-neutral-500">Loading…</div>;

  const when = (b) => `${b.scheduled_date} ${b.scheduled_slot}`;
  const shown = list
    .filter((b) => (tab === "all" ? true : tab === "active" ? ACTIVE.includes(b.status) : b.status === "completed"))
    .sort((a, b) => {
      if (tab === "completed") return String(b.completed_at || "").localeCompare(String(a.completed_at || ""));
      if (tab === "active") return (a.status === "in_progress" ? 0 : 1) - (b.status === "in_progress" ? 0 : 1) || when(a).localeCompare(when(b));
      return String(b.created_at).localeCompare(String(a.created_at));
    });
  const count = (id) => list.filter((b) => (id === "all" ? true : id === "active" ? ACTIVE.includes(b.status) : b.status === "completed")).length;

  const Buttons = ({ b, wide }) => {
    const kinds = actionsFor(b.status);
    if (!kinds.length) return null;
    return (
      <div className={wide ? "grid gap-2 mt-3" : "flex flex-wrap gap-2"} style={wide && kinds.length > 1 ? { gridTemplateColumns: "2fr 1fr" } : undefined}>
        {kinds.map((kind, i) => {
          const a = ACTIONS[kind];
          const sending = busy[b.id] === kind;
          const label = kind === "reject" && kinds[0] === "start" ? "Decline" : a.label;
          return (
            <button key={kind} type="button" disabled={!!busy[b.id]} onClick={() => act(b, kind)}
              className={`inline-flex items-center justify-center gap-1.5 px-3 text-xs font-semibold uppercase tracking-wider disabled:opacity-50 ${wide ? "min-h-[48px]" : "min-h-[40px]"} ${i === 0 ? "bg-black text-white hover:bg-neutral-800" : "border border-neutral-300 bg-white hover:bg-neutral-50"}`}
              data-testid={`seller-booking-${kind}-${b.id}`}>
              <a.icon className="h-4 w-4 flex-shrink-0" /> {sending ? a.busy : label}
            </button>
          );
        })}
      </div>
    );
  };

  const Times = ({ b }) => (b.started_at || b.completed_at) ? (
    <div className="text-xs text-neutral-600 space-y-0.5" data-testid={`seller-booking-times-${b.id}`}>
      {b.started_at && <div>Started: <span className="font-semibold">{formatMoment(b.started_at)}</span></div>}
      {b.completed_at && <div>Ended: <span className="font-semibold">{formatMoment(b.completed_at)}</span>{b.started_at ? ` · ${formatDuration(b.started_at, b.completed_at)}` : ""}</div>}
    </div>
  ) : null;

  const Pill = ({ b }) => (
    <span className={`inline-block px-2 py-1 text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap ${PILL[b.status] || "bg-neutral-100"}`} data-testid={`seller-booking-status-${b.id}`}>
      {statusLabel(b.status)}
    </span>
  );

  return (
    <div data-testid="seller-bookings">
      <div className="admin-overline">Schedule</div>
      <h1 className="admin-heading text-3xl font-bold mt-1 mb-4">My bookings</h1>

      <div className="flex flex-wrap gap-2 mb-5" role="tablist" data-testid="seller-bookings-tabs">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
            className={`px-4 text-xs font-semibold uppercase tracking-wider min-h-[44px] ${tab === id ? "bg-black text-white" : "bg-white border border-neutral-300"}`}
            data-testid={`seller-bookings-tab-${id}`}>
            {label} ({count(id)})
          </button>
        ))}
      </div>

      {shown.length === 0 && (
        <div className="bg-white border border-neutral-200 py-8 px-4 text-center text-sm text-neutral-500" data-testid="seller-bookings-empty">
          {tab === "active" ? "No jobs waiting for you." : tab === "completed" ? "No completed jobs yet." : "No bookings assigned."}
        </div>
      )}

      {/* Phones: one card per booking */}
      <div className="md:hidden space-y-3">
        {shown.map((b) => (
          <div key={b.id} className="bg-white border border-neutral-200 p-4" data-testid={`seller-booking-${b.id}`}>
            <div className="flex justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[11px] text-neutral-500">{b.code}</div>
                <div className="font-semibold break-words">{b.service_name}</div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="font-bold">{formatINR(b.total)}</div>
                <div className="mt-1"><Pill b={b} /></div>
              </div>
            </div>
            <div className="mt-2 text-xs text-neutral-600 space-y-0.5">
              <div className="font-semibold text-neutral-800">{formatDay(b.scheduled_date)} · {b.scheduled_slot}</div>
              <div className="break-words">{b.customer_name} · <a href={`tel:${b.customer_phone}`} className="underline">{b.customer_phone}</a></div>
              {b.address?.line1 && <div className="break-words">{[b.address.line1, b.address.landmark, b.address.city, b.address.pincode].filter(Boolean).join(", ")}</div>}
              {b.notes && <div className="break-words italic">“{b.notes}”</div>}
            </div>
            <div className="mt-2"><Times b={b} /></div>
            <Buttons b={b} wide />
          </div>
        ))}
      </div>

      {shown.length > 0 && (
        <div className="hidden md:block bg-white border border-neutral-200 table-scroll">
          <table className="w-full text-sm">
            <thead className="border-b border-neutral-200 bg-neutral-50"><tr className="text-left">
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Code</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Service</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Customer</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">When</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Status</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold">Task</th>
              <th className="px-3 py-3 text-xs uppercase tracking-wider font-semibold text-right">Total</th>
            </tr></thead>
            <tbody>
              {shown.map((b) => (
                <tr key={b.id} className="border-b border-neutral-100 align-top" data-testid={`seller-booking-row-${b.id}`}>
                  <td className="px-3 py-3 font-mono text-xs">{b.code}</td>
                  <td className="px-3 py-3">{b.service_name}</td>
                  <td className="px-3 py-3 text-xs">{b.customer_name}<br /><a href={`tel:${b.customer_phone}`} className="text-neutral-500 underline">{b.customer_phone}</a>
                    {b.address?.line1 && <div className="text-neutral-500 break-words max-w-[220px]">{[b.address.line1, b.address.city].filter(Boolean).join(", ")}</div>}</td>
                  <td className="px-3 py-3 text-xs whitespace-nowrap">{b.scheduled_date}<br /><span className="text-neutral-500">{b.scheduled_slot}</span></td>
                  <td className="px-3 py-3"><Pill b={b} /></td>
                  <td className="px-3 py-3 space-y-2"><Buttons b={b} /><Times b={b} /></td>
                  <td className="px-3 py-3 font-bold text-right">{formatINR(b.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
